import { TelemetryColors } from '@shared/interfaces/telemetry';

export function hexToRgb(hex: string) {
  const bigint = parseInt(hex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

export function rgbToHex(r: number, g: number, b: number) {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  ).toUpperCase();
}

export function interpolateColor(startHex: string, endHex: string, factor: number): string {
  const startRgb = hexToRgb(startHex);
  const endRgb = hexToRgb(endHex);

  const r = Math.round(startRgb.r + factor * (endRgb.r - startRgb.r));
  const g = Math.round(startRgb.g + factor * (endRgb.g - startRgb.g));
  const b = Math.round(startRgb.b + factor * (endRgb.b - startRgb.b));

  return rgbToHex(r, g, b);
}

// Returns grouped color (index defines color, variation hue)
export const getBlockColors = (index: number, variationIndex = 1, totalVariations = 1): string => {
  const hue = (index * 137.508) % 360; // Use golden angle approximation
  const lightness = (variationIndex / totalVariations) * 85 + 15; // Ligtness between 15% and 100%
  return `hsla(${hue}, 70%, ${lightness}%, 0.65)`;
};

// Returns consistent colors for e.g. graphs
export const getSeriesColor = (index: number): string => {
  const hue = (index * 137.508) % 360; // Use golden angle approximation
  return `hsla(${hue}, 70%, 50%, 0.65)`;
};

const ColorMap: { [key: string]: [number, string][] } = {
  Greens: [
    [0, '#fafffa'],
    [1, '#004703'],
  ],
  Blues: [
    [0, '#f3f1ff'],
    [1, '#110067'],
  ],
  Reds: [
    [0, '#fff1f1'],
    [1, '#670000'],
  ],
  undefined: [
    [0, '#ffffff'],
    [1, '#000000'],
  ],
};

export function getColorScale(colors: [number, string][] | TelemetryColors, intensity: number): string {
  const colorscale = Array.isArray(colors) ? colors : ColorMap[colors];
  const t = Math.max(0, Math.min(1, intensity));
  let lower = colorscale[0];
  let upper = colorscale[colorscale.length - 1];
  for (let i = 0; i < colorscale.length - 1; i++) {
    if (t >= colorscale[i][0] && t <= colorscale[i + 1][0]) {
      lower = colorscale[i];
      upper = colorscale[i + 1];
      break;
    }
  }
  // Interpolate within the segment
  const range = upper[0] - lower[0];
  const seg = range === 0 ? 0 : (t - lower[0]) / range;
  const a = hexToRgb(lower[1]);
  const b = hexToRgb(upper[1]);
  const r = Math.round(a.r + seg * (b.r - a.r));
  const g = Math.round(a.g + seg * (b.g - a.g));
  const bv = Math.round(a.b + seg * (b.b - a.b));
  return `rgb(${r}, ${g}, ${bv})`;
}

export function getThemeColor(color?: string | null) {
  switch (color?.toLocaleLowerCase()) {
    case 'primary':
    case 'blue':
      return '#26788f';
    case 'secondary':
      return '#a400fb';
    case 'tertiary':
      return '#c7481e';
    case 'success':
    case 'green':
      return '#167e16';
    case 'error':
    case 'red':
      return '#cb1d10';
    case 'warning':
    case 'orange':
      return '#ffcc00';
    default:
      return '#26788f';
  }
}
